// Explode view: push bodies outward from the model center along their centroids.
import * as THREE from 'three';
import { clamp } from './math';
import { getModelBoundingBox, getModelGroup, requestRender } from './viewer';

/** At 100% the outermost body moves this many model radii (half the bbox diagonal). */
const EXPLODE_REACH = 1.2;

/**
 * Which way bodies are allowed to travel. 'all' fans the assembly out in
 * every direction; a single axis lays it out along that one, which is how you
 * read a stack of plates or a column of floors.
 */
export type ExplodeAxis = 'all' | 'x' | 'y' | 'z';

type Body = {
  mesh: THREE.Mesh;
  parent: THREE.Object3D;
  /** Local position the body sits at when not exploded. */
  rest: THREE.Vector3;
  /** Its world position at rest, the start of every push. */
  worldRest: THREE.Vector3;
  /** World offset of its centroid from the model center. */
  offset: THREE.Vector3;
  /** Local travel at 100%, recomputed whenever the axis changes. */
  delta: THREE.Vector3;
};

let bodies: Body[] = [];
let modelRadius = 1;
let axis: ExplodeAxis = 'all';
let factor = 0;

const tmpBox = new THREE.Box3();
const tmpCenter = new THREE.Vector3();
const tmpDir = new THREE.Vector3();
const tmpExploded = new THREE.Vector3();
const tmpRestLocal = new THREE.Vector3();
const tmpExplodedLocal = new THREE.Vector3();

/** How far this body is from the center, and which way, along the current axis. */
function travelOf(body: Body): { dir: THREE.Vector3; dist: number } {
  const { offset } = body;
  if (axis === 'all') {
    const dist = offset.length();
    const dir = dist < 1e-6 ? tmpDir.set(0, 0, 1) : tmpDir.copy(offset).multiplyScalar(1 / dist);
    return { dir, dist };
  }
  const component = axis === 'x' ? offset.x : axis === 'y' ? offset.y : offset.z;
  // A body sitting exactly on the plane still has to go somewhere; pick the
  // positive side rather than leaving it buried in the middle.
  const sign = component < 0 ? -1 : 1;
  const dir = tmpDir.set(axis === 'x' ? sign : 0, axis === 'y' ? sign : 0, axis === 'z' ? sign : 0);
  return { dir, dist: Math.abs(component) };
}

/**
 * Works out each body's travel at 100%. The outermost one moves EXPLODE_REACH
 * model radii, the rest in proportion to their distance. Scaling by the model
 * size, rather than by the raw centroid offsets, keeps tightly stacked coaxial
 * parts from barely moving while large assemblies stay within proportion.
 */
function recomputeDeltas(): void {
  let maxDist = 0;
  for (const body of bodies) maxDist = Math.max(maxDist, travelOf(body).dist);

  for (const body of bodies) {
    const { dir, dist } = travelOf(body);
    const push = maxDist > 1e-6 ? modelRadius * EXPLODE_REACH * (dist / maxDist) : 0;
    tmpExploded.copy(body.worldRest).addScaledVector(dir, push);

    tmpRestLocal.copy(body.worldRest);
    body.parent.worldToLocal(tmpRestLocal);
    tmpExplodedLocal.copy(tmpExploded);
    body.parent.worldToLocal(tmpExplodedLocal);

    body.delta.copy(tmpExplodedLocal).sub(tmpRestLocal);
  }
}

/** Moves every body to where the current factor says it belongs. */
function applyFactor(): void {
  for (const body of bodies) {
    body.mesh.position.copy(body.rest).addScaledVector(body.delta, factor);
  }
  requestRender();
}

/** Capture rest poses for every model body. Call after a new model is loaded. */
export function initExplode(): void {
  factor = 0;
  bodies = [];
  const model = getModelGroup();
  const bbox = getModelBoundingBox();
  if (!model || !bbox || bbox.isEmpty()) return;

  model.updateMatrixWorld(true);
  const center = bbox.getCenter(tmpCenter);
  modelRadius = Math.max(bbox.getSize(new THREE.Vector3()).length() * 0.5, 1e-3);

  model.traverse(obj => {
    const mesh = obj as THREE.Mesh;
    if (!mesh.isMesh || !mesh.userData.isModelBody) return;
    tmpBox.setFromObject(mesh);
    if (tmpBox.isEmpty()) return;

    const worldRest = mesh.getWorldPosition(new THREE.Vector3());
    bodies.push({
      mesh,
      parent: mesh.parent ?? model,
      rest: mesh.position.clone(),
      worldRest,
      offset: tmpBox.getCenter(new THREE.Vector3()).sub(center),
      delta: new THREE.Vector3()
    });
  });

  recomputeDeltas();
  applyFactor();
}

export function getExplodeFactor(): number {
  return factor;
}

/** t in [0, 1]. */
export function setExplodeFactor(t: number): void {
  factor = clamp(t, 0, 1);
  applyFactor();
}

export function getExplodeAxis(): ExplodeAxis {
  return axis;
}

/** Switching axis re-aims the travel and leaves the amount where it was. */
export function setExplodeAxis(next: ExplodeAxis): void {
  if (next === axis) return;
  axis = next;
  recomputeDeltas();
  applyFactor();
}

export function resetExplode(): void {
  setExplodeFactor(0);
}

/** True once a model has bodies that can be pushed apart. */
export function canExplode(): boolean {
  return bodies.length > 1;
}
